import { useCallback, useState } from 'react';

export function useUploadSession(api) {
  const [token, setToken] = useState('');
  const [files, setFiles] = useState([]);
  const [quote, setQuote] = useState(null);
  const [uploading, setUploading] = useState({});

  const refreshQuote = useCallback(async (currentFiles, currentToken) => {
    const t = currentToken ?? token;
    if (!t || (currentFiles ?? files).length === 0) {
      setQuote(null);
      return;
    }
    const acceptedFiles = (currentFiles ?? files).filter(f => !f.rejected);
    const quantities = Object.fromEntries(acceptedFiles.map(f => [f.fileId, f.qty]));
    const res = await api.quote(t, quantities);
    setQuote(res.quote);
  }, [api, token, files]);

  const upload = useCallback(async (file) => {
    const tempId = Math.random().toString(36).slice(2);
    setUploading(u => ({
      ...u,
      [tempId]: { name: file.name, sizeBytes: file.size, progress: 0, stage: 'uploading' },
    }));
    let result;
    try {
      result = await api.upload(file, token, (event) => {
        setUploading(u => {
          if (!u[tempId]) return u;
          return {
            ...u,
            [tempId]: { ...u[tempId], stage: event.stage, progress: event.value },
          };
        });
      });
    } catch (e) {
      setUploading(u => { const next = { ...u }; delete next[tempId]; return next; });
      console.error('upload failed', e);
      return;
    }
    setUploading(u => { const next = { ...u }; delete next[tempId]; return next; });

    if (!result.accepted) {
      setFiles(prev => [...prev, {
        fileId: 'err_' + tempId,
        rejected: true,
        originalName: file.name,
        analysis: result.analysis,
      }]);
      return;
    }
    const newToken = result.sessionToken;
    if (!token) setToken(newToken);
    const newFile = {
      fileId: result.fileId,
      originalName: file.name,
      width: result.analysis.width,
      length: result.analysis.length,
      sizeBytes: result.analysis.sizeBytes,
      dpiRating: result.analysis.dpiRating,
      qty: 1,
      warnings: result.analysis.warnings || [],
    };
    const next = [...files, newFile];
    setFiles(next);
    refreshQuote(next, newToken);
  }, [api, token, files, refreshQuote]);

  const remove = useCallback(async (fileId) => {
    if (fileId.startsWith('err_')) {
      const next = files.filter(f => f.fileId !== fileId);
      setFiles(next);
      return;
    }
    await api.remove(token, fileId);
    const next = files.filter(f => f.fileId !== fileId);
    setFiles(next);
    refreshQuote(next, token);
  }, [api, token, files, refreshQuote]);

  const setQty = useCallback(async (fileId, qty) => {
    const next = files.map(f => f.fileId === fileId ? { ...f, qty } : f);
    setFiles(next);
    refreshQuote(next, token);
  }, [files, refreshQuote, token]);

  return { token, files, quote, uploading, upload, remove, setQty };
}
